import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Building2, CheckCircle, AlertCircle, ArrowLeft, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BusinessProfile() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [einVerified, setEinVerified] = useState(false);
  const [einError, setEinError] = useState('');
  const [form, setForm] = useState({
    company_name: '',
    ein: '',
    industry: '',
    business_size: 'small',
    years_in_operation: '',
    annual_revenue: '',
    employee_count: '',
    business_address: '',
    description: ''
  });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const industries = [
    'Technology', 'Healthcare', 'Construction', 'Manufacturing',
    'Retail', 'Food & Beverage', 'Professional Services', 'Transportation',
    'Real Estate', 'Education', 'Agriculture', 'Energy',
    'Financial Services', 'Media & Entertainment', 'Other'
  ];

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await axios.get(`${API}/business/profile`, { headers });
        if (data && data.company_name) {
          setForm({
            company_name: data.company_name || '',
            ein: data.ein_formatted || data.ein || '',
            industry: data.industry || '',
            business_size: data.business_size || 'small',
            years_in_operation: data.years_in_operation?.toString() || '',
            annual_revenue: data.annual_revenue?.toString() || '',
            employee_count: data.employee_count?.toString() || '',
            business_address: data.business_address || '',
            description: data.description || ''
          });
          if (data.ein_verified) setEinVerified(true);
        }
      } catch (err) {
        // No profile yet — that's fine
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token]);

  const verifyEin = async () => {
    setVerifying(true);
    setEinError('');
    try {
      const { data } = await axios.post(`${API}/business/verify-ein`, { ein: form.ein }, { headers });
      setEinVerified(true);
      setForm(prev => ({ ...prev, ein: data.ein }));
      toast({ title: 'EIN Verified', description: data.message });
    } catch (err) {
      setEinError(err.response?.data?.detail || 'Verification failed');
      setEinVerified(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!einVerified) {
      toast({ title: 'Verify EIN first', description: 'Please verify your EIN before saving.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/business/profile`, {
        company_name: form.company_name,
        ein: form.ein,
        industry: form.industry,
        business_size: form.business_size,
        years_in_operation: parseInt(form.years_in_operation),
        annual_revenue: parseFloat(form.annual_revenue),
        employee_count: parseInt(form.employee_count),
        business_address: form.business_address || null,
        description: form.description || null
      }, { headers });
      toast({ title: 'Profile saved!', description: 'Your business profile has been updated.' });
      navigate('/business');
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout title="Business Profile">
      <div data-testid="business-profile-page">
        <Button variant="ghost" size="sm" onClick={() => navigate('/business')} className="mb-4 text-gray-400">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Button>

        <div className="max-w-2xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-1">
              <Building2 className="w-6 h-6 text-purple-400" />
              <h2 className="text-2xl font-bold">Company Profile</h2>
            </div>
            <p className="text-sm text-gray-400 mb-8">Verify your business identity to unlock borrowing. All fields are required.</p>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Details */}
            <div className="p-6 rounded-xl bg-white/[0.03] border border-white/5 space-y-4">
              <h3 className="font-semibold text-sm text-gray-400 uppercase tracking-wider">Company Details</h3>

              <div>
                <Label>Company Name</Label>
                <Input required value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})}
                  className="mt-1 bg-white/5 border-white/10" placeholder="Acme Corporation"
                  data-testid="company-name-input" />
              </div>

              <div>
                <Label>Employer Identification Number (EIN)</Label>
                <div className="flex gap-2 mt-1">
                  <Input required value={form.ein} onChange={(e) => { setForm({...form, ein: e.target.value}); setEinVerified(false); setEinError(''); }}
                    className="bg-white/5 border-white/10" placeholder="XX-XXXXXXX"
                    data-testid="ein-input" />
                  <Button type="button" onClick={verifyEin} disabled={verifying || !form.ein}
                    variant={einVerified ? 'outline' : 'default'}
                    className={einVerified ? 'border-green-500/30 text-green-400' : 'bg-gradient-to-r from-blue-500 to-purple-500'}
                    data-testid="verify-ein-btn">
                    {verifying ? 'Verifying...' : einVerified ? <><CheckCircle className="w-4 h-4 mr-1" /> Verified</> : <><Shield className="w-4 h-4 mr-1" /> Verify</>}
                  </Button>
                </div>
                {einError && (
                  <p className="text-xs text-red-400 mt-1 flex items-center gap-1" data-testid="ein-error">
                    <AlertCircle className="w-3 h-3" /> {einError}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Industry</Label>
                  <Select value={form.industry} onValueChange={(v) => setForm({...form, industry: v})}>
                    <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="industry-select">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Business Size</Label>
                  <Select value={form.business_size} onValueChange={(v) => setForm({...form, business_size: v})}>
                    <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small (1-50 employees)</SelectItem>
                      <SelectItem value="medium">Medium (51-500 employees)</SelectItem>
                      <SelectItem value="large">Large (500+ employees)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Financial Details */}
            <div className="p-6 rounded-xl bg-white/[0.03] border border-white/5 space-y-4">
              <h3 className="font-semibold text-sm text-gray-400 uppercase tracking-wider">Financial Details</h3>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Years in Operation</Label>
                  <Input required type="number" min="0" value={form.years_in_operation}
                    onChange={(e) => setForm({...form, years_in_operation: e.target.value})}
                    className="mt-1 bg-white/5 border-white/10" placeholder="5"
                    data-testid="years-input" />
                </div>
                <div>
                  <Label>Annual Revenue ($)</Label>
                  <Input required type="number" min="0" step="1000" value={form.annual_revenue}
                    onChange={(e) => setForm({...form, annual_revenue: e.target.value})}
                    className="mt-1 bg-white/5 border-white/10" placeholder="2500000"
                    data-testid="revenue-input" />
                </div>
                <div>
                  <Label>Employee Count</Label>
                  <Input required type="number" min="1" value={form.employee_count}
                    onChange={(e) => setForm({...form, employee_count: e.target.value})}
                    className="mt-1 bg-white/5 border-white/10" placeholder="25"
                    data-testid="employees-input" />
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="p-6 rounded-xl bg-white/[0.03] border border-white/5 space-y-4">
              <h3 className="font-semibold text-sm text-gray-400 uppercase tracking-wider">Additional Information</h3>

              <div>
                <Label>Business Address (Optional)</Label>
                <Input value={form.business_address} onChange={(e) => setForm({...form, business_address: e.target.value})}
                  className="mt-1 bg-white/5 border-white/10" placeholder="123 Main St, City, State, ZIP"
                  data-testid="address-input" />
              </div>
              <div>
                <Label>Business Description (Optional)</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                  className="mt-1 bg-white/5 border-white/10" rows={3}
                  placeholder="Briefly describe what your business does..." data-testid="description-input" />
              </div>
            </div>

            <Button type="submit" disabled={saving || !einVerified}
              className="w-full h-12 bg-gradient-to-r from-purple-500 to-pink-500 text-base font-semibold"
              data-testid="save-profile-btn">
              {saving ? 'Saving...' : 'Save Business Profile'}
            </Button>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
